import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Diamond, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      navigate(u.role === "admin" ? "/admin" : (loc.state?.from || "/app"));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <Diamond size={20} className="text-[#D4AF37]" />
          <span className="font-display text-2xl">LT Global Club</span>
        </Link>
        <div className="lt-card p-8">
          <div className="overline">Sign in</div>
          <h2 className="font-display text-3xl mt-2 mb-6">Welcome back.</h2>
          <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label className="overline block mb-2">User ID or Email</label>
              <input
                className="lt-input"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <div className="relative">
                <input
                  className="lt-input pr-10"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-200"
                  data-testid="toggle-password-visibility"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
            <button className="lt-btn-primary w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="text-sm text-neutral-400 mt-6 text-center">
            New here? <Link to="/register" data-testid="login-go-register" className="gold-text">Create an account</Link>
          </div>
          <div className="text-xs text-neutral-600 mt-4 text-center">
            Forgot your password? Contact admin for a reset.
          </div>
        </div>
      </div>
    </div>
  );
}
